import './editableTag.scss';
import React, {KeyboardEventHandler, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import classNames from 'clsx';
import {Spinner} from '../spinner/spinner';
import {getOpenstreetmapUrl, Keyname} from '../../utils/utils';
import {HibuTag} from '../../search/search-types';
import {SvgIcon} from '../../icons/svgIcon';
import {Icons} from '../../icons/icons';
import {useStateTechnicalDetails} from '../../settings/duck/settings-hooks';
import {renderActionButtons} from '../../search/search-results/search-result-details/details/details.utils';
import {useConfirm, useWithConfirmation} from '../hibuModal/hibuModal';

export type FormFieldType = 'line' | 'multiline' | 'number' | 'boolean';

type InputProps = {
    fieldType: FormFieldType;
    value: string;
    onSubmit: (newValue: string) => void;
    onUserValidation: () => void;
    onDelete: () => void;
    onCancel: () => void;
    allowEditing?: boolean;
    userValidationEnabled?: boolean;
    userValidated?: boolean;
    valueLink?: string;
    tagGroupName?: string;
    tag?: HibuTag;
};

export const EditableTag = React.memo((props: InputProps) => {
    const {t} = useTranslation();
    const showTechnicalDetails = useStateTechnicalDetails();

    const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
    const [localValue, setLocalValue] = useState<string>(props.value);
    const [editModeActive, setEditModeActive] = useState<boolean>(false);

    const onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
        setLocalValue(e.target.value);
    };

    useEffect(() => {
        setLocalValue(props.value);
    }, [props.value]);

    const skipBlurEventRef = useRef(false);
    const timeoutRef = useRef<number | null>(null);

    // clean up after unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current !== null) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const onSubmit = () => {
        skipBlurEventRef.current = true;
        setLoadingUpdate(true);
        if (localValue !== props.value) {
            if (timeoutRef.current !== null) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            props.onSubmit(localValue);
        } else {
            props.onCancel();
        }
        setLoadingUpdate(false);
    };

    const confirm = useConfirm();

    const onBlur = () => {
        if (skipBlurEventRef.current) return;
        if (localValue !== props.value) {
            timeoutRef.current = window.setTimeout(async () => {
                timeoutRef.current = null;
                const ok = await confirm(t('submittable-input.modal.text'), {
                    title: t('submittable-input.modal.title'),
                    ok: t('general.save'),
                    cancel: t('modal.discard'),
                    backdrop: true,
                    keyboard: true,
                    closeButton: false,
                });
                if (ok) onSubmit();
                else props.onCancel();
            }, 300);
        } else {
            props.onCancel();
        }
        setEditModeActive(false);
    };

    const onTextAreaKeyDown: KeyboardEventHandler = (e) => {
        if (e.key === Keyname.ENTER && (props.fieldType !== 'multiline' || e.ctrlKey)) {
            onSubmit();
            e.stopPropagation();
            (e.currentTarget as any).blur();
        }
    };

    const isNumberField: boolean = props.fieldType === 'number';
    const isTextField = props.fieldType === 'line' || props.fieldType === 'multiline';
    const isAddingNewTag = !props.tag;

    const renderWikidataLink = (tagCategory: string, tag: HibuTag) => {
        if (
            tag.id &&
            showTechnicalDetails &&
            tagCategory !== 'doctype' &&
            tag.id.startsWith('wikidata') &&
            tag.extra?.userValidated_bool === undefined
        ) {
            const wikidataId = tag.id.substring(tag.id.lastIndexOf(':') + 1);
            const wikidataUrl = 'https://www.wikidata.org/wiki/' + wikidataId;
            return (
                <a href={wikidataUrl} target="_blank" rel="noopener noreferrer" className="tag-link-icon">
                    ({wikidataId})
                </a>
            );
        }
        return null;
    };

    const renderLocationLink = (tagCategory: string, tag: HibuTag) => {
        if (showTechnicalDetails && tagCategory === 'location' && tag.extra?.coordinates && tag.extra?.area_tokenized_string) {
            const url = getOpenstreetmapUrl(tag);
            return (
                <a href={url} target="_blank" rel="noopener noreferrer" className="tag-link-icon">
                    <SvgIcon icon={Icons.MAP}/>
                </a>
            );
        }
        return null;
    };

    const renderTag = (value: string, tag: HibuTag | undefined, tagCategory: string | undefined) => {
        if (!value) {
            return;
        }
        return tag && tagCategory ? (
            <>
                {value} {renderWikidataLink(tagCategory, tag)} {renderLocationLink(tagCategory, tag)}
            </>
        ) : (
            <>{value}</>
        );
    };

    const {onClick: buildDeleteClick} = useWithConfirmation(
        t('fieldTitle.tags.modal.deleteTag.message', {
            group: t('fieldTitle.tagsPlural.' + props.tagGroupName + '.label'),
            tag: props.tag?.label
        }),
        {
            backdrop: true,
            keyboard: true,
            closeButton: false,
            title: t('fieldTitle.tags.modal.deleteTag.title'),
            cancel: t('general.cancel'),
            ok: t('general.clear'),
        }
    );

    return (
        <div
            className={classNames(
                'submittable-input',
                props.fieldType,
                loadingUpdate ? 'loading-update' : '',
                editModeActive ? 'edit-active' : '',
                props.allowEditing ? 'action-enabled' : '',
            )}
        >
            {props.allowEditing ? (
                <>
                    {isNumberField && (
                        <input
                            autoFocus
                            disabled={loadingUpdate}
                            onChange={onChange}
                            type="number"
                            value={localValue}
                            onKeyDown={onTextAreaKeyDown}
                            onBlur={onBlur}
                        />
                    )}
                    {isTextField && (
                        <textarea
                            autoFocus={isAddingNewTag}
                            disabled={loadingUpdate}
                            onChange={onChange}
                            onFocus={() => {
                                skipBlurEventRef.current = false;
                                setEditModeActive(true);
                            }}
                            placeholder=""
                            value={localValue}
                            onKeyDown={onTextAreaKeyDown}
                            onBlur={onBlur}
                        />
                    )}
                    {loadingUpdate ? (
                        <div className={'submit-update-indicator'}>
                            <Spinner/>
                        </div>
                    ) : (
                        renderActionButtons(
                            props.onUserValidation,
                            props.onDelete,
                            t,
                            props.userValidated,
                            props.userValidationEnabled,
                            buildDeleteClick(() => props.onDelete())
                        )
                    )}
                </>
            ) : (
                <>
                    {props.valueLink ? (
                        <a href={props.valueLink} target="_blank" rel="noopener noreferrer" className="tag-link-icon">
                            {props.value}
                        </a>
                    ) : (
                        <div className="tag-content">{renderTag(props.value, props.tag, props.tagGroupName)}</div>
                    )}
                </>
            )}
        </div>
    );
});
